// The site's colors, derived from the listing's three colors (website-basics-colors spec §6).
// Text only ever uses text, mutedText, link, onPrimary or onAccent, so every text
// pairing on the page is one the app's accessibility grade measured.
import { useEffect, useMemo, type CSSProperties } from 'react';
import { useBooking } from './useBooking';
import {
  AA,
  SITE_COLOR_DEFAULTS,
  contrastRatio,
  deriveSiteColors,
  isDark,
  nearestLightness,
  normalizeHex,
  type SiteColorInputs,
  type SiteColorTokens,
} from '../lib/siteColors';
import { hexToRgba } from '../lib/colorUtils';
import type { BusinessListing } from '../types';

/** Missing or invalid colors fall back to the defaults; there is no migration from older listings. */
export function listingColorInputs(listing: BusinessListing | null): SiteColorInputs {
  return {
    background: normalizeHex(listing?.background_color) ?? SITE_COLOR_DEFAULTS.background,
    primary: normalizeHex(listing?.primary_color) ?? SITE_COLOR_DEFAULTS.primary,
    accent: normalizeHex(listing?.accent_color) ?? SITE_COLOR_DEFAULTS.accent,
  };
}

export function useSiteColors(): SiteColorTokens {
  const { businessListing } = useBooking();
  const { background, primary, accent } = listingColorInputs(businessListing);
  return useMemo(() => deriveSiteColors({ background, primary, accent }), [background, primary, accent]);
}

// Status hues for errors, warnings and confirmations. Each is moved to the nearest
// shade that reads on the page (with headroom for its soft tint), keeping the hue.
const STATUS_HUES = { danger: '#DC2626', warning: '#B45309', success: '#15803D' } as const;
const STATUS_MIN_CONTRAST = 5.5;

function readableOn(hue: string, background: string): string {
  if (contrastRatio(hue, background) >= STATUS_MIN_CONTRAST) return hue;
  const passes = (shade: string) => contrastRatio(shade, background) >= Math.max(AA, STATUS_MIN_CONTRAST);
  return nearestLightness(hue, passes, isDark(background) ? 'lighter' : 'darker') ?? hue;
}

const STATUS_VARIABLES = (c: SiteColorTokens): [string, string][] =>
  (Object.keys(STATUS_HUES) as (keyof typeof STATUS_HUES)[]).flatMap((name): [string, string][] => {
    const color = readableOn(STATUS_HUES[name], c.background);
    return [
      [`--site-${name}`, color],
      [`--site-${name}-soft`, hexToRgba(color, 0.12)],
    ];
  });

// shadcn's own variables, so ui components (e.g. the date picker) follow the site colors too
const SHADCN_VARIABLES = (c: SiteColorTokens): [string, string][] => [
  ['--background', c.background],
  ['--foreground', c.text],
  ['--card', c.background],
  ['--card-foreground', c.text],
  ['--popover', c.background],
  ['--popover-foreground', c.text],
  ['--primary', c.primaryFill],
  ['--primary-foreground', c.onPrimary],
  ['--secondary', hexToRgba(c.text, 0.06)],
  ['--secondary-foreground', c.text],
  ['--muted', hexToRgba(c.text, 0.06)],
  ['--muted-foreground', c.mutedText],
  ['--accent', hexToRgba(c.text, 0.08)],
  ['--accent-foreground', c.text],
  ['--border', c.border],
  ['--input', c.border],
  ['--ring', c.link],
];

const CSS_VARIABLES: [string, (c: SiteColorTokens) => string][] = [
  ['--site-bg', (c) => c.background],
  ['--site-text', (c) => c.text],
  ['--site-text-muted', (c) => c.mutedText],
  ['--site-primary', (c) => c.primaryFill],
  ['--site-on-primary', (c) => c.onPrimary],
  ['--site-primary-outline', (c) => c.primaryOutline ?? 'transparent'],
  ['--site-link', (c) => c.link],
  ['--site-accent', (c) => c.accent],
  ['--site-on-accent', (c) => c.onAccent],
  ['--site-border', (c) => c.border],
];

/** The tokens for a listing loaded outside BookingProvider (e.g. the standalone form page). */
export function siteColorsFor(listing: BusinessListing | null): SiteColorTokens {
  return deriveSiteColors(listingColorInputs(listing));
}

/**
 * Publishes the tokens as --site-* custom properties on the root element (Tailwind's
 * `site-*` colors read them), plus status colors and shadcn's variables.
 */
export function useSiteColorVariables(colors: SiteColorTokens) {
  useEffect(() => {
    const root = document.documentElement.style;
    const entries: [string, string][] = [
      ...CSS_VARIABLES.map(([name, value]): [string, string] => [name, value(colors)]),
      ...STATUS_VARIABLES(colors),
      ...SHADCN_VARIABLES(colors),
    ];
    for (const [name, value] of entries) root.setProperty(name, value);
    return () => {
      for (const [name] of entries) root.removeProperty(name);
    };
  }, [colors]);
}

export function SiteColorVariables() {
  useSiteColorVariables(useSiteColors());
  return null;
}

/** A primary (Book) button: fill, label, an outline when the fill would vanish into the page, and a soft glow. */
export function primaryButtonStyle(c: SiteColorTokens, glow = 0.25): CSSProperties {
  return {
    backgroundColor: c.primaryFill,
    color: c.onPrimary,
    border: c.primaryOutline ? `1px solid ${c.primaryOutline}` : undefined,
    boxShadow: glow ? `0 4px 24px ${hexToRgba(c.primaryFill, glow)}` : undefined,
  };
}

/** A badge or pill in the accent color. */
export function accentPillStyle(c: SiteColorTokens): CSSProperties {
  return { backgroundColor: c.accent, color: c.onAccent };
}
